import { Avatar, Icon, Menu, Wordmark } from "@platform/ui";
import { StepPanel, useSession } from "@platform/ui/features";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useTranslations } from "use-intl";
import { type QueueCounts, useQueueCounts } from "../lib/queues";
import { type NavItem, visibleNav } from "./nav";

/** Where this build runs, set by its bundle job (`VITE_PLATFORM_ENV`); a build without it is local. */
export const ENVIRONMENT: "local" | "staging" | "production" =
  import.meta.env.VITE_PLATFORM_ENV === "production"
    ? "production"
    : import.meta.env.VITE_PLATFORM_ENV === "staging"
      ? "staging"
      : "local";

const ENVIRONMENT_CLASS = {
  local: "bg-console-line text-console-ink",
  staging: "bg-env-staging text-console-bar",
  production: "bg-env-production text-on-brand",
} as const;

/** The sidebar's groups (card 110): the queues, the look-ups, and the platform's settings. */
const GROUPS: readonly { key: string; label?: string; items: readonly NavItem["key"][] }[] = [
  { key: "overview", items: ["queues"] },
  {
    key: "queues",
    label: "admin.shell.groups.queues",
    items: ["verification", "productReviews", "disputes"],
  },
  { key: "lookUp", label: "admin.shell.groups.lookUp", items: ["enterprises", "orders", "products"] },
  {
    key: "platform",
    label: "admin.shell.groups.platform",
    items: ["categories", "riskLists", "settings", "audit"],
  },
];

const COUNTED: Partial<Record<NavItem["key"], keyof QueueCounts>> = {
  verification: "verification",
  productReviews: "productReviews",
  disputes: "disputes",
  riskLists: "riskMatches",
};

/** An order number goes to the orders; anything else to the enterprises, by name or registration number. */
export function lookUpPath(text: string): string {
  const q = encodeURIComponent(text.trim());
  return /^ORD-/i.test(text.trim()) ? `/orders?q=${q}` : `/enterprises?q=${q}`;
}

/**
 * Every page but sign-in needs an operator session. The console's frame (docs/design/meridian.md, Layout): a 48 px
 * dark bar with the wordmark, "Console", the environment (amber for staging, red for production), the look-up, and
 * the operator; a 216 px dark sidebar of queues with counts, look-ups, and the platform.
 */
export function OperatorLayout() {
  const t = useTranslations();
  const { state } = useSession();
  const location = useLocation();
  const counts = useQueueCounts();
  // Operators hold cookie sessions, which are never offline; the state is shown like loading.
  if (state.status === "loading" || state.status === "offline")
    return <StepPanel title={t("common.loading")}>{null}</StepPanel>;
  if (state.status === "signed_out")
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  const visible = visibleNav(state.claims.perm);
  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <ConsoleBar />
      <div className="flex min-h-0 flex-1">
        <nav
          aria-label={t("admin.shell.name")}
          className="flex w-54 shrink-0 flex-col gap-0.5 bg-console-side px-2.5 py-3"
        >
          {GROUPS.map((group) => {
            const items = visible.filter((item) => (group.items as readonly string[]).includes(item.key));
            if (items.length === 0) return null;
            return (
              <div key={group.key} className="flex flex-col gap-0.5">
                {group.label !== undefined && (
                  <span className="px-2.5 pt-3.5 pb-1.5 text-tag font-semibold tracking-[0.05em] text-console-muted uppercase">
                    {t(group.label)}
                  </span>
                )}
                {items.map((item) => {
                  const counted = COUNTED[item.key];
                  const count = counted === undefined ? undefined : counts[counted];
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={"end" in item}
                      aria-describedby={count !== undefined && count > 0 ? `queue-${item.key}` : undefined}
                      className={({ isActive }) =>
                        `flex h-8 items-center gap-2.5 rounded-(--radius-chip) px-2.5 text-small transition-colors duration-(--duration-fast) ${
                          isActive
                            ? "bg-console-active font-semibold text-console-ink"
                            : "text-console-text hover:bg-console-field hover:text-console-ink"
                        }`
                      }
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {t(item.key === "queues" ? "admin.shell.overview" : `admin.nav.${item.key}`)}
                      </span>
                      {count !== undefined && count > 0 && (
                        <>
                          <span aria-hidden="true" className="text-tag text-console-muted tabular-nums">
                            {count}
                          </span>
                          <span id={`queue-${item.key}`} hidden>
                            {t("admin.shell.waiting", { count })}
                          </span>
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <main className="min-w-0 flex-1 px-6 py-5">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function ConsoleBar() {
  const t = useTranslations();
  const { session } = useSession();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [operator, setOperator] = useState<{ name: string; email: string; roles: readonly string[] }>();

  useEffect(() => {
    void session.api.GET("/v1/ops/me").then(({ data }) => {
      if (data !== undefined) setOperator(data);
    });
  }, [session]);

  const environment = t(`admin.shell.environment.${ENVIRONMENT}`);
  const role = operator?.roles[0];
  return (
    <header className="flex h-12 shrink-0 items-center gap-3 bg-console-bar px-4 text-console-ink">
      <Wordmark label={t("common.productName")} height={13} />
      <span aria-hidden="true" className="h-4.5 w-px bg-console-line" />
      <span className="text-body font-semibold">{t("admin.shell.name")}</span>
      <span
        role="status"
        aria-label={t("admin.shell.environmentLabel", { name: environment })}
        className={`rounded-[4px] px-1.5 text-tag font-bold tracking-[0.04em] ${ENVIRONMENT_CLASS[ENVIRONMENT]}`}
      >
        {environment}
      </span>
      <search className="ms-auto w-80 max-w-[40vw]">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (text.trim() !== "") navigate(lookUpPath(text));
          }}
          className="flex h-7.5 items-center gap-2 rounded-(--radius-control) bg-console-field px-2.5 focus-within:outline-2 focus-within:outline-brand-500"
        >
          <Icon icon={Search} className="size-3.5 shrink-0 text-console-muted" />
          <input
            type="search"
            aria-label={t("admin.shell.lookUp")}
            placeholder={t("admin.shell.lookUpPlaceholder")}
            value={text}
            onChange={(event) => setText(event.target.value)}
            className="min-w-0 flex-1 bg-transparent text-small text-console-ink outline-none placeholder:text-console-muted"
          />
        </form>
      </search>
      {operator !== undefined && (
        <Menu
          label={t("shell.personMenu")}
          placement="bottom-end"
          header={
            <div className="flex flex-col">
              <span className="text-small font-semibold text-ink">{operator.name}</span>
              <span className="text-caption text-ink-muted">{operator.email}</span>
            </div>
          }
          items={[{ key: "sign-out", label: t("common.signOut"), onSelect: () => void session.signOut() }]}
          trigger={(props) => (
            <button
              type="button"
              {...props}
              aria-label={t("shell.person", { name: operator.name })}
              className="flex items-center gap-2 rounded-(--radius-control) px-1.5 py-1 text-small text-console-text hover:bg-console-field"
            >
              <Avatar name={operator.name} size={24} className="bg-console-active text-console-ink" />
              <span>
                {operator.name}
                {role !== undefined && ` · ${t(`admin.shell.roles.${role}`)}`}
              </span>
            </button>
          )}
        />
      )}
    </header>
  );
}
